"use client"

import {
  Fragment,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useDeferredValue,
  type ReactNode,
} from "react"
import Link from "next/link"
import { m, AnimatePresence, LazyMotion, domMax } from "framer-motion"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CrossCatalogSuggestion } from "@/components/ui/cross-catalog-suggestion"
import {
  contentTokens as contentTokensOf,
  normalizeForSearch,
  tokenizeForSearch as tokenizeSearch,
} from "@/lib/search/normalize"
import { TabSwitcher } from "@/components/ui/tab-switcher"
import { ActionBadge } from "@/components/ui/action-badge"
import { ArrowRightIcon, Button, buttonVariants } from "@/components/ui/button"
import {
  ChevronRightSmIcon,
  ChevronDownSmIcon,
  SearchIcon,
  CloseIcon,
  VirusIcon,
  TestTubeIcon,
  BrainIcon,
} from "@/components/ui/icons"
import { Highlight } from "@/components/ui/highlight"
import { cn } from "@/lib/utils"
import {
  getCMSBiomarkers,
  getCMSDiseases,
  getCMSCategories,
  getCMSSymptoms,
  getCMSSymptomGroups,
  cmsLocale,
} from "@/lib/cms"
import type {
  CMSBiomarkerListItem,
  CMSDisease,
  CmsCategory,
  CmsSymptomGroup,
  CmsSymptomListItem,
} from "@/lib/cms"
import { SymptomsExplorer } from "@/app/(frontend)/[country]/[locale]/(default-nav)/symptoms/symptoms-explorer-client"
import { buildSymptomPackages } from "@/lib/symptom-packages"
import { useCountry } from "@/components/layout/country-provider"
import { visibleInCountry } from "@/lib/country-gates"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { track } from "@/lib/analytics"
import {
  loadCrossLocaleAliases,
  type CrossLocaleAliasMap,
} from "@/lib/search/cross-locale-aliases-client"
import { buildSymptomAliasMap } from "@/lib/search/symptom-aliases"
import { areSymptomsEnabled } from "@/i18n/config"
import { hasSymptomsPreviewActive } from "@/lib/symptoms-preview"
import {
  PACKAGE_FILTER_ORDER,
  PACKAGE_LABELS,
  PACKAGE_TO_TEST_SLUG,
  indexCmsCategories,
} from "@/lib/categories"
import { renderCategoryIcon } from "@/lib/category-icons"
import { PackageIcon } from "@/components/ui/package-icons"

/* ── Types ── */

type DiseaseItem = {
  /** Payload doc id — locale-invariant identity (slug is localized, so it
   *  cannot be used to identify a row across locales). Optional because the
   *  fallback-disease path has no CMS id. */
  id?: string
  name: string
  slug?: string
  description?: string | null
}
type BiomarkerItem = {
  /** Payload doc id — the stable, locale-invariant identity. The `slug` is a
   *  LOCALIZED field (AHD, 2026-08-12), so it must never be used to match a
   *  biomarker across locales; `addonIds` narrowing keys off this instead. */
  id: string
  name: string
  slug: string
  description: string
  /** CMS package memberships, carried so the explorer can tell a test's own
   *  biomarkers from those only available via an add-on (addon-* packages). */
  packages?: string[]
}
type CategoryGroup<T> = { category: string; items: T[] }

/* ── Constants ── */

const MOBILE_ITEM_LIMIT = 10
// Desktop caps the active section at ~6 rows; the 2-col grid makes that 12
// items, then "show more" reveals the rest. Keeping the active section short
// leaves room below for the cropped peek of the next section (no internal
// scroll — the card grows to its natural height ≈ one screen).
const DESKTOP_ITEM_LIMIT = 12
// In search mode the right pane shows cross-category results; cap them to
// roughly fill the card, then a "show more" reveals the full list.
const SEARCH_RESULT_CAP = 14

/** Coloured circle per MRI package, matching the /biomarkers filter chips. */
const PACKAGE_ICON_VARIANT: Record<string, "green" | "blue" | "pink"> = {
  core: "green",
  advanced: "blue",
  pro: "pink",
}

/** Lowercase + strip diacritics for search matching. Lifted from the /faq
 *  search lib (lib/faq-search.ts) — inlined to avoid pulling in fuse.js for
 *  this one use case. Mirrors the membership browser's behavior so the
 *  search still hits "fasting glucose" against a "Glucose" name with
 *  "fasting" in its description, and ignores Swiss umlaut differences. */

/* ── Merge helper ── */

/**
 * Group items by their `category` slug and order the resulting sections by the
 * CMS `categories` collection order (the sole source of truth). Sections whose
 * slug has a CMS row come first in CMS order; any slug with no CMS row is
 * appended after, sorted by item count, so nothing ever vanishes. The
 * `CategoryGroup.category` field carries the slug — the render resolves the
 * localized name/icon/colour from the CMS index by that slug.
 */
function groupByCmsCategory<T extends { name: string }>(
  itemsBySlug: { category: string; items: T[] }[],
  categories: CmsCategory[]
): CategoryGroup<T>[] {
  const merged = new Map<string, T[]>()
  for (const p of itemsBySlug) {
    const prev = merged.get(p.category) || []
    merged.set(p.category, [...prev, ...p.items])
  }
  const sorted = new Map<string, T[]>()
  for (const [slug, items] of merged.entries()) {
    sorted.set(
      slug,
      items.slice().sort((a, b) => a.name.localeCompare(b.name))
    )
  }
  const ordered = categories
    .filter((c) => sorted.has(c.slug) && sorted.get(c.slug)!.length > 0)
    .slice()
    .sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)
    )
    .map((c) => ({ category: c.slug, items: sorted.get(c.slug)! }))
  const knownSlugs = new Set(categories.map((c) => c.slug))
  const extras = Array.from(sorted.entries())
    .filter(([slug, items]) => !knownSlugs.has(slug) && items.length > 0)
    .map(([category, items]) => ({ category, items }))
    .sort((a, b) => b.items.length - a.items.length)
  return [...ordered, ...extras]
}

/* ── Shared row component ── */

export function ExpandableRow({
  name,
  slug,
  description,
  expandedKey,
  expanded,
  onToggle,
  detailPrefix,
  learnMoreLabel,
  bordered = true,
  accent = "aubergine",
  searchTokens,
  collapsedExcerpt,
  trailing,
  expandedContent,
  className,
}: {
  name: string
  slug?: string
  description?: string | null
  expandedKey: string
  expanded: string | null
  onToggle: (key: string | null) => void
  detailPrefix: string
  learnMoreLabel: string
  /** Optional right-aligned content shown before the chevron (e.g. "included
   *  in" package chips on the biomarkers listing). */
  trailing?: ReactNode
  /** Optional content rendered inside the expanded body, between the
   *  description and the "learn more" link (e.g. AI question chips). */
  expandedContent?: ReactNode
  /** Show the bottom divider between rows. Default true. Set false when
   *  the surrounding layout already provides separation (e.g. a card grid
   *  on the membership page). */
  bordered?: boolean
  /** Color of the active/hover accents. Default "aubergine" matches the
   *  test page; "neutral" uses shades of grey for the membership page. */
  accent?: "aubergine" | "neutral"
  /** When the row is rendered inside an active search, pass the active
   *  tokens to highlight them in the name + description. */
  searchTokens?: string[]
  /** Search only: a 1-line description excerpt shown under the title while
   *  collapsed, so a description-only match is visible without expanding. */
  collapsedExcerpt?: string
  /** Extra classes merged onto the row's outer wrapper. Use this for
   *  visibility classes (`hidden md:block` etc.) — wrapping the row in a
   *  parent <div> just to apply them defeats `last:border-b-0`, which is
   *  what gives Eugene's "subtle line between biomarkers" look. */
  className?: string
}) {
  const canExpand = !!description
  const isExpanded = expanded === expandedKey
  const wrapperClass = cn(
    bordered && "border-b border-neutral-100 last:border-b-0",
    className
  )
  const isNeutral = accent === "neutral"
  const rowHoverText = isNeutral
    ? "hover:text-neutral-900"
    : "hover:text-aubergine-900"
  const chevronIdle = isNeutral
    ? "text-neutral-300 group-hover:text-neutral-600"
    : "text-neutral-300 group-hover:text-aubergine-500"
  const chevronActive = isNeutral ? "text-neutral-700" : "text-aubergine-500"
  const learnMoreClass = isNeutral
    ? "text-neutral-900 hover:text-neutral-700"
    : "text-aubergine-700 hover:text-aubergine-900"
  // Bump weight slightly when the row is open so the active marker name
  // reads as the focal item.
  const titleClass = isExpanded ? "font-medium" : ""

  // Render the name (and description) with `<mark>` highlights when an
  // active search is in progress. Falls back to the plain string when no
  // tokens are passed.
  const tokens = searchTokens && searchTokens.length > 0 ? searchTokens : null
  const renderName = () =>
    tokens ? <Highlight text={name} tokens={tokens} /> : name
  const renderDescription = (text: string) =>
    tokens ? <Highlight text={text} tokens={tokens} /> : text

  // Non-expandable case: no description but `slug` is set → render as a
  // link to the detail page so the row stays interactive even before CMS
  // descriptions are wired up. Without slug or description, render plain.
  if (!canExpand) {
    if (slug) {
      return (
        <div className={wrapperClass}>
          <Link
            href={`${detailPrefix}/${slug}`}
            className={cn(
              "type-body-lg group flex w-full items-center justify-between py-2.5 text-left text-neutral-700 transition-colors",
              rowHoverText
            )}
          >
            <span>{renderName()}</span>
            {trailing && (
              <span className="ml-auto flex shrink-0 items-center pl-3">
                {trailing}
              </span>
            )}
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              className={cn(
                "ml-3 shrink-0 transition-colors duration-300",
                chevronIdle
              )}
            >
              <path d="M5 3l4 4-4 4" />
            </svg>
          </Link>
        </div>
      )
    }
    return (
      <div className={wrapperClass}>
        <div className="type-body-lg py-2.5 text-neutral-700">
          {renderName()}
        </div>
      </div>
    )
  }

  return (
    <div className={wrapperClass}>
      <button
        type="button"
        onClick={() => onToggle(isExpanded ? null : expandedKey)}
        className={cn(
          "type-body-lg group flex w-full cursor-pointer items-center justify-between py-2.5 text-left text-neutral-700 transition-colors",
          rowHoverText
        )}
      >
        <span className="flex min-w-0 flex-col">
          <span className={titleClass}>{renderName()}</span>
          {collapsedExcerpt && !isExpanded && (
            <span className="type-body-sm mt-0.5 line-clamp-1 text-neutral-400">
              {renderDescription(collapsedExcerpt)}
            </span>
          )}
        </span>
        {trailing && (
          <span className="ml-auto flex shrink-0 items-center pl-3">
            {trailing}
          </span>
        )}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          className={cn(
            "ml-3 shrink-0 transition-all duration-300",
            isExpanded ? `rotate-90 ${chevronActive}` : chevronIdle
          )}
        >
          <path d="M5 3l4 4-4 4" />
        </svg>
      </button>
      {/* safe-animate-presence: conditional-mount — gated by `isExpanded && description` */}
      <AnimatePresence>
        {isExpanded && description && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
            className="overflow-hidden"
          >
            <div className="pt-1 pr-8 pb-6">
              <p className="type-body-md leading-relaxed text-neutral-500">
                {renderDescription(description)}
              </p>
              {expandedContent}
              {slug && (
                <Link
                  href={`${detailPrefix}/${slug}`}
                  className={cn(
                    "type-body-md mt-4 inline-flex items-center gap-1 font-medium transition-colors",
                    learnMoreClass
                  )}
                >
                  {learnMoreLabel} <ChevronRightSmIcon />
                </Link>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ── Main component ── */

type FallbackDiseaseCategory = {
  category: string
  diseases: { name: string; slug?: string }[]
}

type BiomarkerDiseaseExplorerProps = {
  /** CMS package IDs to filter by (e.g. ["blood"], ["core","advanced","pro"]) */
  packages: string[]
  /** Whether disease descriptions/links are enabled (server-side feature flag) */
  diseaseContentEnabled?: boolean
  /** Which tab to show first. Defaults to "diseases" when disease data exists, else "biomarkers". */
  defaultTab?: "diseases" | "biomarkers"
  /** Local disease data to show while CMS loads or when CMS is unavailable */
  fallbackDiseases?: FallbackDiseaseCategory[]
  /** Treat as blood test for subheading copy. Auto-detected from packages when not set. */
  isBloodTest?: boolean
  /** Per-tab content overrides. Each field falls back to a sensible default
   *  from the `tests` namespace when omitted. CTAs are tab-scoped so the
   *  link below the list updates when the visitor toggles tabs. */
  biomarkersTab?: TabContent
  diseasesTab?: TabContent
  /** Restrict which tabs are available. When set to a single tab, the
   *  diseases-vs-biomarkers TabSwitcher is hidden and the matching CMS
   *  data set is the only one fetched. Useful on biomarkers-only surfaces
   *  like the membership panel. Defaults to ["diseases", "biomarkers"]. */
  tabsAllowed?: ("diseases" | "biomarkers")[]
  /** Optional eyebrow above the heading (e.g. "WHAT WE MEASURE"). */
  eyebrow?: string
  /** Optional outer "panel" tab strip rendered between the eyebrow and the
   *  category browser — used by the membership "Comprehensive blood test
   *  / Follow-up" toggle. The owning page controls active state and feeds
   *  different `packages` per panel via the `key={activePanel}` pattern. */
  panelTabs?: {
    value: string
    label: string
    image?: string
  }[]
  activePanel?: string
  onPanelChange?: (value: string) => void
  /** Slot rendered between the panel tabs and the category browser. Used by
   *  the membership page for the "Follow-up note" callout. */
  panelNote?: React.ReactNode
  /** DOM id on the outer <section> — used for in-page anchors (e.g. the
   *  membership page links to `#what-we-test`). */
  id?: string
  /** Pre-fetched biomarkers from the parent (server component). When
   *  provided, the explorer renders the matching subset on the first paint
   *  instead of waiting for its own client-side CMS fetch — eliminates the
   *  flash-of-nothing on consumers like /membership. */
  seedBiomarkers?: CMSBiomarkerListItem[]
  /** Restrict which ADD-ON biomarkers appear, by **Payload doc id**. Only
   *  meaningful when `packages` includes an `addon-*` package alongside a base
   *  package. The single-biomarker landing pages use it to pull in the base
   *  blood panel plus their own marker (e.g. `["190"]` for cortisol) without
   *  listing the sibling add-ons that share the `addon-single` package. Omit to
   *  keep every add-on the packages resolve to. Base-package items are never
   *  affected.
   *
   *  NB: this keys off the doc `id`, NOT the `slug` — biomarker slugs are
   *  LOCALIZED (a marker's slug differs per locale, e.g. cortisol is
   *  `cortisol-level` in en but `cortisol-morning` in de), so a hard-coded slug
   *  matched the subject marker only in the locale it was written for and
   *  silently filtered it out everywhere else. The doc id is locale-invariant. */
  addonIds?: string[]
  /** Per-category blurb shown under an active pill on desktop. Keys are the
   *  category slugs (== CMS slug, e.g. "cardiovascular"). When omitted, falls
   *  back to the CMS category description. Membership passes the short
   *  conversational blurbs Eugene wrote (`Ihr Herzkrankheitsrisiko und
   *  wie...`) which read better in the master-detail layout than the SEO copy
   *  used on the disease explorer. */
  categoryBlurbs?: Record<string, string>
  /** Tailwind type-scale class for the main section heading. Defaults to the
   *  standard primary-section size `type-display-md`; a consumer can pass a
   *  different scale when a specific layout calls for it. */
  headingSizeClass?: string
  /** Suppress the explorer's own eyebrow/heading/subheading block. Use when
   *  the surrounding page already renders a section header above the explorer
   *  (e.g. the /go/immunity coverage section), so the built-in "Biomarkers we
   *  test" header doesn't duplicate it. Defaults to false — every other
   *  consumer keeps its internal header. */
  hideHeader?: boolean
  /** Emphasize an add-on-only category (e.g. Hormones): a dark "Add-on" badge in
   *  the rail, and the category's add-on markers rendered as a full list under
   *  "Available as separate add-ons:" instead of the truncated pointer. Also
   *  opens that category on first paint when no `defaultCategorySlug` names one
   *  (the female-hormones LP doesn't have to know the add-on's slug). */
  emphasizeAddonCategory?: boolean
  /** CMS category slug (e.g. "cardiovascular", "metabolic") to open on first
   *  paint instead of the first section. Applied once, after the categories
   *  load; user clicks after that are respected. Used by concern LPs
   *  (e.g. /go/heart-health, /go/metabolism) so the explorer lands on the
   *  category the page is about. Takes precedence over the
   *  `emphasizeAddonCategory` auto-pick. */
  defaultCategorySlug?: string
  /** Package chip (e.g. "core") to preselect on first paint instead of "All".
   *  Ignored unless it is one of this explorer's own package chips, so it is a
   *  no-op on single-package surfaces. The full-body MRI page passes "core" so
   *  the counts match the entry price shown above the explorer (AHD-13182). */
  defaultPackageFilter?: string
}

type TabContent = {
  heading?: string
  subheading?: React.ReactNode
  cta?: {
    label: string
    href: string
    /** Visual style of the CTA button. "link" (default) is a thin
     *  text-only link with arrow; "outline" matches the membership
     *  "See all biomarkers" outline button. */
    variant?: "link" | "outline" | "text"
  }
}

export function BiomarkerDiseaseExplorer({
  packages,
  diseaseContentEnabled = true,
  defaultTab,
  fallbackDiseases,
  isBloodTest,
  biomarkersTab,
  diseasesTab,
  tabsAllowed,
  eyebrow,
  panelTabs,
  activePanel,
  onPanelChange,
  panelNote,
  id,
  seedBiomarkers,
  addonIds,
  categoryBlurbs,
  defaultCategorySlug,
  defaultPackageFilter,
  headingSizeClass = "type-display-md",
  hideHeader = false,
  emphasizeAddonCategory = false,
}: BiomarkerDiseaseExplorerProps) {
  const ut = useTranslations("ui")
  const tt = useTranslations("tests")
  const wt = useTranslations("whatWeDetect")
  const st = useTranslations("symptoms")
  const locale = useLocale()
  const { code: country } = useCountry()
  const lp = useLocalePath()

  const allowDiseases = !tabsAllowed || tabsAllowed.includes("diseases")
  const allowBiomarkers = !tabsAllowed || tabsAllowed.includes("biomarkers")

  // A test lists its own package(s); the blood test additionally lists the
  // optional blood add-on packages (addon-*) so the explorer can surface
  // what's available alongside it. Biomarkers that belong ONLY to an add-on
  // must NOT inflate this test's counts or sit inline with the base panel —
  // they appear as a linked "included in <add-on>" snippet instead. When the
  // product *is* an add-on (no non-addon package), there's nothing to split,
  // so everything counts as base.
  const basePackages = useMemo(
    () => packages.filter((p) => !p.startsWith("addon-")),
    [packages]
  )
  const splitAddons = basePackages.length > 0
  /**
   * Package-intersection filter, plus the optional `addonIds` narrowing.
   *
   * A single-biomarker landing page wants the whole base panel (so the "one
   * marker in a full-body blood test" copy is true) AND its own add-on marker,
   * but not its siblings — the `addon-single` package holds all three of
   * cortisol / Lp(a) / PSA, so pulling the package in unfiltered would list the
   * other two on every one of those pages. When `addonIds` is set, add-on-only
   * items survive solely if their Payload doc id is listed; base items are
   * untouched. Keyed off the doc id (not the localized slug) so the subject
   * marker survives in every locale — see the `addonIds` prop docs.
   */
  const keepBiomarker = useCallback(
    (b: CMSBiomarkerListItem) => {
      if (!b.packages.some((p) => packages.includes(p))) return false
      if (!addonIds) return true
      const addonOnly = !b.packages.some((p) => basePackages.includes(p))
      return !addonOnly || addonIds.includes(String(b.id))
    },
    [packages, basePackages, addonIds]
  )
  const isAddonItem = useCallback(
    (item: BiomarkerItem | DiseaseItem) => {
      // A marker named in `addonIds` is the SUBJECT of the page, not an
      // upsell alongside it — it renders inline as a normal row and counts,
      // rather than moving to the "included in <add-on>" snippet. (That snippet
      // would also render label-less and link-less here: `addon-single` has no
      // PACKAGE_LABELS / PACKAGE_TO_TEST_SLUG entry, since one package covers
      // all three single-biomarker pages.) Matched by doc id — diseases have no
      // id here and never carry an add-on package, so they never match.
      if (item.id && addonIds?.includes(String(item.id))) return false
      // Diseases carry no package info → never treated as add-on.
      const pkgs = "packages" in item ? item.packages : undefined
      return (
        splitAddons &&
        !!pkgs?.length &&
        !pkgs.some((p) => basePackages.includes(p))
      )
    },
    [splitAddons, basePackages, addonIds]
  )
  /** The add-on package an item belongs to (for grouping + the link). Prefer an
   *  add-on this explorer actually surfaces — markers shared across panels (e.g.
   *  estradiol in both the women's and men's hormone add-ons) must group under
   *  the one requested via `packages`, not whichever appears first. */
  const addonPackageOf = useCallback(
    (item: { packages?: string[] }) => {
      const addons = (item.packages ?? []).filter((p) => p.startsWith("addon-"))
      return addons.find((p) => packages.includes(p)) ?? addons[0]
    },
    [packages]
  )

  // Package filter chips (Core / Advanced / Pro on the full-body MRI). Derived
  // from this explorer's own non-add-on packages and rendered in the shared
  // PACKAGE_FILTER_ORDER, so only the MRI-relevant packages appear — never the
  // whole catalog the /biomarkers listing shows. Shown only when the test spans
  // more than one base package, which scopes the chips to the MRI surfaces and
  // leaves single-package surfaces (e.g. the blood test) untouched. Selecting a
  // chip narrows the displayed biomarkers + health risks (and the tab counts)
  // to that package, include-based — identical to /biomarkers.
  const packageFilters = useMemo(
    () =>
      basePackages.length > 1
        ? PACKAGE_FILTER_ORDER.filter((p) => basePackages.includes(p.id)).map(
            (p) => ({ id: p.id, label: wt(p.translationKey as never) })
          )
        : [],
    [basePackages, wt]
  )
  const showPackageFilter = packageFilters.length > 1
  const [activePackageFilter, setActivePackageFilter] = useState<string | null>(
    () =>
      showPackageFilter &&
      defaultPackageFilter &&
      packageFilters.some((p) => p.id === defaultPackageFilter)
        ? defaultPackageFilter
        : null
  )

  const [cmsBiomarkers, setCmsBiomarkers] = useState<
    CMSBiomarkerListItem[] | null
  >(() => {
    if (!seedBiomarkers || packages.length === 0) return null
    const relevant = seedBiomarkers.filter(keepBiomarker)
    return relevant.length > 0 ? relevant : null
  })
  const [cmsDiseases, setCmsDiseases] = useState<CMSDisease[] | null>(null)
  // CMS-managed category names + descriptions (sole source of truth). Fetched
  // client-side like the biomarker/disease catalogs above; section headings
  // resolve via `catText` once loaded.
  const [cmsCategories, setCmsCategories] = useState<CmsCategory[]>([])
  // Full, UNFILTERED catalogs for the zero-result cross-catalog fallback.
  // Seed-based pages never run the package-filtered fetch effect, so these
  // are fetched lazily on the first zero-result search (see effect below).
  const [allBiomarkers, setAllBiomarkers] = useState<
    CMSBiomarkerListItem[] | null
  >(null)
  const [allDiseases, setAllDiseases] = useState<CMSDisease[] | null>(null)
  const [crossMatch, setCrossMatch] = useState<
    import("@/lib/search/cross-catalog").CrossCatalogMatch | null
  >(null)
  const [activeCategory, setActiveCategory] = useState(0)
  // Settled on the sidebar rail layout. Kept as a typed value (not inlined)
  // so the existing `layoutMode === "sidebar"` conditionals still read clearly.
  const [layoutMode] = useState<"sidebar" | "tags-top">("sidebar")
  const [search, setSearch] = useState("")
  const deferredSearch = useDeferredValue(search)
  const [expandedItem, setExpandedItem] = useState<string | null>(null)
  const [showAllItems, setShowAllItems] = useState(false)
  const [showAllSearch, setShowAllSearch] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  function navigateToCategory(nextIndex: number) {
    if (nextIndex === activeCategory) return
    setActiveCategory(nextIndex)
    setExpandedItem(null)
    setShowAllItems(false)
    // Bring the section heading up to just below the sticky navbar so the
    // newly-selected category reads from the top (the search stays pinned in
    // the left rail). Smooth so a click on the bottom "Next" peek is legible.
    const el = sectionRef.current
    if (el) {
      const NAV_OFFSET = 88
      const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" })
    }
  }

  const hasLocalDiseases =
    allowDiseases &&
    fallbackDiseases !== undefined &&
    fallbackDiseases.length > 0 &&
    fallbackDiseases.some((c) => c.diseases.length > 0)
  const hasDiseases =
    allowDiseases &&
    ((cmsDiseases !== null && cmsDiseases.length > 0) || hasLocalDiseases)
  const hasBiomarkers =
    allowBiomarkers && cmsBiomarkers !== null && cmsBiomarkers.length > 0
  const initialTab: "diseases" | "biomarkers" =
    defaultTab ?? (allowBiomarkers ? "biomarkers" : "diseases")
  const [activeTab, setActiveTab] = useState<"diseases" | "biomarkers">(
    initialTab
  )
  const [tabInitialized, setTabInitialized] = useState(!!defaultTab)
  // Symptoms is surfaced as a peer tab but kept off the `activeTab` union (which
  // is threaded through every biomarker/disease memo); a separate flag swaps the
  // inline symptoms view in without disturbing that machinery.
  const [symptomsMode, setSymptomsMode] = useState(false)
  // `CmsSymptomListItem`, not `CmsSymptom`: this is filled from
  // `getCMSSymptoms`, whose URL carries SYMPTOM_LIST_SELECT and so never
  // returns `sources` / `relatedArticles`. Both are optional on `CmsSymptom`,
  // which is exactly why the wrong annotation compiled. The type has to be
  // chosen here, it will not be caught.
  const [cmsSymptoms, setCmsSymptoms] = useState<CmsSymptomListItem[]>([])
  const [cmsSymptomGroups, setCmsSymptomGroups] = useState<CmsSymptomGroup[]>(
    []
  )
  // Full biomarker/disease sets for the symptom → test (package) mapping that
  // drives the inline symptoms view's filter chips. Fetched alongside the
  // symptoms so the mapping is consistent regardless of the explorer's own
  // package-scoped biomarker/disease loading.
  const [symptomBio, setSymptomBio] = useState<CMSBiomarkerListItem[]>([])
  const [symptomDis, setSymptomDis] = useState<CMSDisease[]>([])

  /**
   * Value-identity for the `packages` prop. Every consumer passes an inline
   * array literal, so the reference changes on each parent render; using the
   * array directly as an effect dependency re-ran the CMS fetches on every
   * re-render. The set is a handful of short slugs, so joining is cheap and
   * exact.
   */
  const packagesKey = packages.join("\0")

  // When no explicit defaultTab, auto-select diseases once data loads
  useEffect(() => {
    if (tabInitialized) return
    if (allowDiseases && hasDiseases) {
      setActiveTab("diseases")
      setTabInitialized(true)
    }
  }, [hasDiseases, tabInitialized, allowDiseases])

  // Fetch CMS data filtered by packages
  // CMS categories — sole source of truth for section names + descriptions.
  useEffect(() => {
    getCMSCategories(cmsLocale(locale, country)).then(setCmsCategories)
  }, [locale, country])

  const cmsIndex = useMemo(
    () => indexCmsCategories(cmsCategories),
    [cmsCategories]
  )
  // Sections are keyed by category slug (== CMS slug). Resolve the slug to its
  // CMS-managed localized name + description + icon/colour. A slug with no CMS
  // row (an unseeded "extra" category) renders its raw slug as the heading — a
  // visible-but-unstyled fallback, never a dropped section.
  const catCmsDoc = (slug: string): CmsCategory | undefined =>
    cmsIndex.get(slug)
  const catText = (slug: string): { name: string; description: string } => {
    const doc = catCmsDoc(slug)
    if (!doc) return { name: slug, description: "" }
    return { name: doc.name, description: doc.description ?? "" }
  }

  useEffect(() => {
    if (packages.length === 0) return
    const cl = cmsLocale(locale, country)
    if (allowBiomarkers && !seedBiomarkers) {
      // Re-bucket each biomarker onto its granular category by slug. Lets the
      // explorer render the new fine-grained groups against the live CMS
      // before the data migration (scripts/migrate-biomarker-categories.ts).
      // The CMS index is per-locale; `hideInCountries` is per-country
      // (AHD-12853), so every client fetch here filters on the active market
      // before anything is rendered, counted or linked.
      getCMSBiomarkers(cl).then((fetched) => {
        const biomarkers = visibleInCountry(fetched, country)
        setAllBiomarkers(biomarkers)
        const relevant = biomarkers.filter(keepBiomarker)
        if (relevant.length > 0) setCmsBiomarkers(relevant)
      })
    }
    if (allowDiseases) {
      getCMSDiseases(cl).then((diseases) => {
        setAllDiseases(diseases)
        const relevant = diseases.filter((d) =>
          d.packages.some((p) => packages.includes(p))
        )
        if (relevant.length > 0) setCmsDiseases(relevant)
      })
    }
    // `packagesKey`, not `packages`: every consumer passes an inline array
    // literal (`/go/checkup` passes `packages={["core", "advanced", "pro"]}`),
    // so the prop is a fresh reference on each parent render and this effect
    // re-fired every time — re-downloading the full biomarker AND disease
    // lists. That is where /go/checkup's duplicate CMS requests came from
    // (AHD-9214); it is a re-render, not two competing fetchers. Comparing by
    // value makes the effect run once per genuine package-set change.
    //
    // exhaustive-deps wants `packages` itself here; that is exactly the bug.
    // `packagesKey` is derived from it and is the value-identity we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    packagesKey,
    locale,
    country,
    allowBiomarkers,
    allowDiseases,
    seedBiomarkers,
    keepBiomarker,
  ])

  // Group diseases by category slug (CMS preferred, local fallback). Section
  // order comes from the CMS `categories` collection.
  const diseaseCategories = useMemo(() => {
    if (cmsDiseases && cmsDiseases.length > 0) {
      const grouped = new Map<string, DiseaseItem[]>()
      for (const d of cmsDiseases) {
        if (activePackageFilter && !d.packages.includes(activePackageFilter))
          continue
        const items = grouped.get(d.category) || []
        items.push({
          id: String(d.id),
          name: d.name,
          slug: d.slug,
          description: d.description ?? undefined,
        })
        grouped.set(d.category, items)
      }
      const partial = Array.from(grouped.entries()).map(
        ([category, items]) => ({ category, items })
      )
      return groupByCmsCategory(partial, cmsCategories)
    }
    if (fallbackDiseases && fallbackDiseases.length > 0) {
      const partial = fallbackDiseases.map((c) => ({
        category: c.category,
        items: c.diseases.map(
          (d): DiseaseItem => ({
            name: d.name,
            slug: d.slug,
            description: undefined,
          })
        ),
      }))
      return groupByCmsCategory(partial, cmsCategories)
    }
    return []
  }, [cmsDiseases, fallbackDiseases, cmsCategories, activePackageFilter])

  // Group biomarkers by category slug; section order from the CMS collection.
  const biomarkerCategories = useMemo(() => {
    if (!cmsBiomarkers) return []
    const grouped = new Map<string, BiomarkerItem[]>()
    cmsBiomarkers.forEach((b) => {
      if (activePackageFilter && !b.packages.includes(activePackageFilter))
        return
      const list = grouped.get(b.category) || []
      list.push({
        id: String(b.id),
        name: b.name,
        slug: b.slug,
        description: b.description,
        packages: b.packages,
      })
      grouped.set(b.category, list)
    })
    const partial = Array.from(grouped.entries()).map(([category, items]) => ({
      category,
      items,
    }))
    return groupByCmsCategory(partial, cmsCategories)
  }, [cmsBiomarkers, cmsCategories, activePackageFilter])

  // Switching package filter reshuffles which categories exist, so the active
  // index can fall out of range — reset to the first section (and collapse any
  // open row / "show more") the same way a tab switch does.
  useEffect(() => {
    setActiveCategory(0)
    setExpandedItem(null)
    setShowAllItems(false)
  }, [activePackageFilter])

  // Tokenized + diacritic-stripped search. Each token must appear
  // somewhere in the haystack (AND across tokens, OR across name +
  // description + slug-as-words), so "fasting glucose" matches a "Glucose"
  // marker with "fasting" in its description.
  const searchTokens = useMemo(
    () => tokenizeSearch(deferredSearch),
    [deferredSearch]
  )
  const searching = searchTokens.length > 0

  // Lazy-loaded cross-locale name maps (other locales' names per slug), so the
  // zero-result cross-catalog fallback has the same cross-language reach as the
  // in-scope page search. Fetched as static JSON on mount rather than bundled
  // into route JS — see lib/search/cross-locale-aliases-client.ts.
  const [bioAliases, setBioAliases] = useState<CrossLocaleAliasMap>({})
  const [diseaseAliases, setDiseaseAliases] = useState<CrossLocaleAliasMap>({})
  useEffect(() => {
    loadCrossLocaleAliases("biomarker").then(setBioAliases)
    loadCrossLocaleAliases("disease").then(setDiseaseAliases)
  }, [])

  // Symptom-aware search augmentation. Each biomarker/disease's tagged symptom
  // names (+ synonyms when populated) become extra searchable aliases so e.g.
  // typing "fatigue" surfaces the items tagged with it. Gated behind
  // `areSymptomsEnabled()` so it's inert until launch (empty map ⇒ no effect).
  const symptomsEnabled = areSymptomsEnabled()
  // Surface the symptoms lens as a peer tab when the lens is on (build-time
  // env) OR the visitor opted into the preview via the `?symptoms-preview=`
  // magic link (JS-readable companion cookie, read post-mount so SSR stays
  // static — never a server `cookies()` read, see lib/symptoms-preview.ts).
  const [symptomsPreview, setSymptomsPreview] = useState(false)
  useEffect(() => {
    if (!symptomsEnabled && hasSymptomsPreviewActive()) setSymptomsPreview(true)
  }, [symptomsEnabled])
  const showSymptomsTab = symptomsEnabled || symptomsPreview
  // Load the symptoms lens data so the inline tab is ready on first click.
  // Gated on tab visibility (enabled && (public || preview cookie)) — NOT just
  // the build flag — so a non-preview visitor on a behind-token deploy never
  // fires these CMS requests (which would leak the lens over the network).
  useEffect(() => {
    if (!showSymptomsTab) return
    const cl = cmsLocale(locale, country)
    getCMSSymptoms(cl).then(setCmsSymptoms)
    getCMSSymptomGroups(cl).then(setCmsSymptomGroups)
    getCMSBiomarkers(cl).then((b) =>
      setSymptomBio(visibleInCountry(b, country))
    )
    getCMSDiseases(cl).then(setSymptomDis)
  }, [showSymptomsTab, locale, country])
  const symptomPackages = useMemo(
    () => buildSymptomPackages(cmsSymptoms, symptomBio, symptomDis),
    [cmsSymptoms, symptomBio, symptomDis]
  )
  // Count of symptoms this test covers — the Symptoms tab is scoped to the test,
  // like the Health risks / Biomarkers tabs.
  const scopedSymptomCount = useMemo(() => {
    if (!packages.length) return cmsSymptoms.length
    return cmsSymptoms.filter((s) =>
      (symptomPackages[s.slug] ?? []).some((p) => packages.includes(p))
    ).length
  }, [cmsSymptoms, symptomPackages, packages])
  // Symptom search aliases only augment search when the lens is reachable by
  // this visitor (public or preview cookie) — otherwise typing a symptom name
  // would surface tagged items and leak the lens on a behind-token deploy.
  const bioSymptomAliases = useMemo(
    () => buildSymptomAliasMap(allBiomarkers ?? [], showSymptomsTab),
    [allBiomarkers, showSymptomsTab]
  )
  const diseaseSymptomAliases = useMemo(
    () => buildSymptomAliasMap(allDiseases ?? [], showSymptomsTab),
    [allDiseases, showSymptomsTab]
  )

  // Content tokens = the words that actually discriminate. Used only by the
  // partial fallback below; the strict pass still uses every token.
  const contentTokens = useMemo(
    () => contentTokensOf(searchTokens),
    [searchTokens]
  )

  const haystackFor = (
    name: string,
    description?: string | null,
    slug?: string
  ) => {
    const symptomAliases =
      slug && showSymptomsTab
        ? ((activeTab === "diseases"
            ? diseaseSymptomAliases
            : bioSymptomAliases)[slug] ?? [])
        : []
    return [
      normalizeForSearch(name),
      description ? normalizeForSearch(description) : "",
      slug ? normalizeForSearch(slug.replace(/-/g, " ")) : "",
      ...symptomAliases.map(normalizeForSearch),
    ]
      .filter(Boolean)
      .join(" ")
  }

  /** Strict: every token must appear. The precise pass, tried first. */
  const matchesItemStrict = (
    name: string,
    description?: string | null,
    slug?: string
  ) => {
    if (!searching) return true
    const haystack = haystackFor(name, description, slug)
    return searchTokens.every((t) => haystack.includes(t))
  }

  /** Partial: any content token. Only reached when the strict pass found
   *  nothing anywhere — see `partialActive`. */
  const matchesItemPartial = (
    name: string,
    description?: string | null,
    slug?: string
  ) => {
    if (!searching) return true
    const haystack = haystackFor(name, description, slug)
    return contentTokens.some((t) => haystack.includes(t))
  }

  // Cross-category search results — grouped by category, in the same
  // order as the left rail.
  type SearchGroup =
    | { category: string; kind: "diseases"; items: DiseaseItem[] }
    | { category: string; kind: "biomarkers"; items: BiomarkerItem[] }
  const buildGroups = (
    match: (name: string, description?: string | null, slug?: string) => boolean
  ): SearchGroup[] => {
    if (activeTab === "diseases") {
      return diseaseCategories.map((cat) => ({
        category: cat.category,
        kind: "diseases" as const,
        items: cat.items.filter((d) => match(d.name, d.description, d.slug)),
      }))
    }
    return biomarkerCategories.map((cat) => ({
      category: cat.category,
      kind: "biomarkers" as const,
      // Add-on-only matches are handled separately (addonSearchMatches) and
      // never count toward this test's search totals.
      items: cat.items.filter(
        (b) => !isAddonItem(b) && match(b.name, b.description, b.slug)
      ),
    }))
  }

  // Strict AND across every token was the whole matcher until 2026-08-07, and
  // it hard-fails on any multi-word query carrying a filler word or a term we
  // don't stock: "complete blood count", "oral glucose tolerance test",
  // "antikörper schilddrüse" were all real 30d dead ends on these explorers.
  // Fall back to "any content token" ONLY when strict found nothing anywhere,
  // so a precise query is never diluted — the user sees the closest markers we
  // do have instead of an empty widget.
  const strictGroups = useMemo<SearchGroup[] | null>(() => {
    if (!searching) return null
    return buildGroups(matchesItemStrict)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    diseaseCategories,
    biomarkerCategories,
    searching,
    searchTokens,
    isAddonItem,
  ])

  const partialActive =
    strictGroups !== null &&
    strictGroups.every((g) => g.items.length === 0) &&
    searchTokens.length > 1 &&
    contentTokens.length > 0

  const activeMatch = partialActive ? matchesItemPartial : matchesItemStrict

  const searchGroups = useMemo<SearchGroup[] | null>(() => {
    if (!searching) return null
    if (!partialActive) return strictGroups
    return buildGroups(matchesItemPartial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, partialActive, strictGroups, contentTokens])

  // Filtered items for current category
  const filteredItems = useMemo(() => {
    if (activeTab === "diseases") {
      const cat = diseaseCategories[activeCategory]
      if (!cat) return []
      if (!searching) return cat.items
      return cat.items.filter((d) => activeMatch(d.name, d.description, d.slug))
    }
    const cat = biomarkerCategories[activeCategory]
    if (!cat) return []
    // Add-on-only biomarkers are normally excluded from the inline list (they
    // surface as a linked snippet at the bottom). But when the add-on category
    // is emphasized (e.g. Hormones on the female-hormones LP), we render its
    // add-on markers as the full list — same form as every other category.
    const nonAddon = cat.items.filter((b) => !isAddonItem(b))
    const base =
      emphasizeAddonCategory && nonAddon.length === 0 && cat.items.length > 0
        ? cat.items
        : nonAddon
    if (!searching) return base
    return base.filter((b) => activeMatch(b.name, b.description, b.slug))
    // activeMatch is closed over searchTokens via `searching`; eslint
    // can't see the closure, so it's fine to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    diseaseCategories,
    biomarkerCategories,
    activeCategory,
    searching,
    searchTokens,
    partialActive,
    isAddonItem,
    emphasizeAddonCategory,
  ])

  const totalSearchHits = useMemo(
    () =>
      searchGroups ? searchGroups.reduce((n, g) => n + g.items.length, 0) : 0,
    [searchGroups]
  )

  // Add-on-only biomarkers that match the query. Not counted as hits — shown
  // as "Looking for X? It's included in <add-on>" pointers so a search for an
  // add-on marker leads the user to the right add-on instead of a dead end.
  const addonSearchMatches = useMemo(() => {
    if (!searching || activeTab !== "biomarkers" || !splitAddons) return []
    const seen = new Set<string>()
    const out: { item: BiomarkerItem; pkg: string }[] = []
    for (const cat of biomarkerCategories) {
      for (const b of cat.items) {
        if (!isAddonItem(b)) continue
        // Strict on purpose: this drives the "Looking for X?" pointer and
        // the search_suggestion_shown event, so it must stay precise even
        // when the main list has fallen back to partial matching.
        if (!matchesItemStrict(b.name, b.description, b.slug)) continue
        const pkg = addonPackageOf(b)
        if (!pkg) continue
        const key = b.slug ?? b.name
        if (seen.has(key)) continue
        seen.add(key)
        out.push({ item: b, pkg })
      }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searching,
    activeTab,
    splitAddons,
    biomarkerCategories,
    searchTokens,
    isAddonItem,
    addonPackageOf,
  ])

  // Shared renderer for the add-on pointer list (used in both the
  // zero-result and the with-results search states).
  const addonHintList =
    addonSearchMatches.length > 0 ? (
      <ul className="flex flex-col gap-2 border-t border-neutral-100 pt-5">
        {addonSearchMatches.map(({ item, pkg }) => {
          const addonLabel = PACKAGE_LABELS[pkg] ?? pkg
          const addonSlug = PACKAGE_TO_TEST_SLUG[pkg]
          const text = wt("searchAddonHint", {
            name: item.name,
            addon: addonLabel,
          })
          return (
            <li key={item.slug ?? item.name}>
              {addonSlug ? (
                <Link
                  href={lp(`/tests/${addonSlug}`)}
                  className="type-body-md inline-flex items-center gap-1 text-neutral-600 transition-colors hover:text-neutral-900"
                >
                  {text}
                  <ChevronRightSmIcon className="size-4" />
                </Link>
              ) : (
                <span className="type-body-md text-neutral-600">{text}</span>
              )}
            </li>
          )
        })}
      </ul>
    ) : null

  // Browse mode: the active biomarker category's add-on-only markers, shown as
  // a linked "included in <add-on>" snippet beneath the base list (e.g. Omega
  // and Hormones on the blood test, which have no base markers of their own).
  const addonBrowseSnippet = (() => {
    if (activeTab !== "biomarkers") return null
    const activeCat = biomarkerCategories[activeCategory]
    if (!activeCat) return null
    // When the add-on category is emphasized, its markers already render as the
    // full inline list — skip the truncated "X, Y and N more" pointer.
    if (
      emphasizeAddonCategory &&
      activeCat.items.length > 0 &&
      activeCat.items.every((it) => isAddonItem(it))
    )
      return null
    const byPkg = new Map<string, BiomarkerItem[]>()
    for (const it of activeCat.items) {
      if (!isAddonItem(it)) continue
      const pkg = addonPackageOf(it)
      if (!pkg) continue
      const list = byPkg.get(pkg) ?? []
      list.push(it)
      byPkg.set(pkg, list)
    }
    if (byPkg.size === 0) return null
    return (
      <div className="mt-8 border-t border-neutral-100 pt-6">
        <p className="type-body-lg font-medium text-neutral-900">
          {wt("addonOnlyNote")}
        </p>
        <div className="mt-6 flex flex-col gap-8">
          {Array.from(byPkg.entries()).map(([pkg, items]) => {
            const addonLabel = PACKAGE_LABELS[pkg] ?? pkg
            const addonSlug = PACKAGE_TO_TEST_SLUG[pkg]
            // Emphasized add-on categories (e.g. Hormones on the female-hormones
            // LP) list every marker; elsewhere it stays a "X, Y and N more" peek.
            const limit = emphasizeAddonCategory ? items.length : 2
            const names = items
              .slice(0, limit)
              .map((it) => it.name)
              .join(", ")
            const rest = items.length - Math.min(limit, items.length)
            const includedIn = wt("addonIncludedIn", { addon: addonLabel })
            return (
              <div key={pkg} className="flex flex-col items-start gap-4">
                <span className="type-body-lg text-neutral-700">
                  {names}
                  {rest > 0
                    ? ` ${wt("addonAndMore", { count: String(rest) })}`
                    : ""}
                </span>
                {addonSlug ? (
                  <Link href={lp(`/tests/${addonSlug}`)}>
                    <Button
                      variant="outline"
                      shape="round"
                      iconRight={<ArrowRightIcon />}
                    >
                      {includedIn}
                    </Button>
                  </Link>
                ) : (
                  <Button variant="outline" shape="round">
                    {includedIn}
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  })()

  // Zero-result fallback. Dynamic-import the Fuse-backed matcher so the
  // explorer's hot path keeps its deliberate no-Fuse bundle. Runs only once
  // the scoped search is genuinely empty.
  //
  // The matcher needs the FULL unfiltered catalog. Seed-based pages (test
  // detail) render from `seedBiomarkers` and never run the fetch effect, so
  // `allBiomarkers`/`allDiseases` stay null there — fetch the full catalog
  // lazily on the first zero-result search and cache it for later queries.
  useEffect(() => {
    const q = deferredSearch.trim()
    if (!q || totalSearchHits > 0) {
      setCrossMatch(null)
      return
    }
    let cancelled = false
    const cl = cmsLocale(locale, country)
    async function resolveCatalog() {
      if (activeTab === "biomarkers") {
        if (allBiomarkers) return allBiomarkers
        const fetched = visibleInCountry(await getCMSBiomarkers(cl), country)
        if (!cancelled) setAllBiomarkers(fetched)
        return fetched
      }
      if (allDiseases) return allDiseases
      const fetched = await getCMSDiseases(cl)
      if (!cancelled) setAllDiseases(fetched)
      return fetched
    }
    resolveCatalog().then(async (all) => {
      if (cancelled) return
      const { findCrossCatalogMatches } =
        await import("@/lib/search/cross-catalog")
      if (cancelled) return
      const aliasMap = activeTab === "diseases" ? diseaseAliases : bioAliases
      const symptomAliasMap =
        activeTab === "diseases" ? diseaseSymptomAliases : bioSymptomAliases
      const matches = findCrossCatalogMatches({
        query: q,
        allItems: all,
        locale,
        augmentAliases: (item) => [
          // Cross-locale names are keyed by the locale-invariant doc id; the
          // symptom-alias map is same-locale, so it stays keyed by slug.
          ...(aliasMap[String(item.id)] ?? []),
          ...(symptomAliasMap[item.slug] ?? []),
        ],
        excludePackages: packages, // the test's own packages
        limit: 1,
      })
      const next = matches[0] ?? null
      setCrossMatch(next)
      if (next) {
        track.searchSuggestionShown({
          query: q,
          surface:
            activeTab === "biomarkers"
              ? "explorer-biomarkers"
              : "explorer-diseases",
          match_count: matches.length,
          type: activeTab,
          locale,
        })
      }
    })
    return () => {
      cancelled = true
    }
  }, [
    deferredSearch,
    totalSearchHits,
    activeTab,
    allBiomarkers,
    allDiseases,
    locale,
    country,
    packages,
    bioAliases,
    diseaseAliases,
    bioSymptomAliases,
    diseaseSymptomAliases,
  ])

  // Track searches (debounced; deduped on same query+count+tab). Mirrors the
  // FAQ pattern in faq-client.tsx — fires only after the user pauses typing.
  // Distinct surfaces per active tab so PostHog can split biomarker vs
  // disease searches inside this shared component (used on /tests/[slug],
  // /membership, and various landing pages).
  const lastTrackedSearchRef = useRef<{
    query: string
    count: number
    tab: "biomarkers" | "diseases"
  } | null>(null)
  useEffect(() => {
    const q = deferredSearch.trim()
    if (q.length < 3) return
    const handle = setTimeout(() => {
      const last = lastTrackedSearchRef.current
      if (
        last &&
        last.query === q &&
        last.count === totalSearchHits &&
        last.tab === activeTab
      )
        return
      lastTrackedSearchRef.current = {
        query: q,
        count: totalSearchHits,
        tab: activeTab,
      }
      track.searchQuery({
        query: q,
        result_count: totalSearchHits,
        surface:
          activeTab === "biomarkers"
            ? "explorer-biomarkers"
            : "explorer-diseases",
        locale,
        zero_results: totalSearchHits === 0,
      })
    }, 1200)
    return () => clearTimeout(handle)
  }, [deferredSearch, totalSearchHits, activeTab, locale])

  // Auto-jump active category to the first group with matches when the
  // search query *changes* — but not on every render, so a user can click
  // a different category while a search is active without snapping back.
  const lastJumpedQueryRef = useRef("")
  useEffect(() => {
    const q = deferredSearch.trim()
    if (!q || !searchGroups) {
      lastJumpedQueryRef.current = ""
      return
    }
    if (lastJumpedQueryRef.current === q) return
    lastJumpedQueryRef.current = q
    const firstHit = searchGroups.findIndex((g) => g.items.length > 0)
    if (firstHit >= 0) setActiveCategory(firstHit)
  }, [deferredSearch, searchGroups])

  // A new query (or tab switch) starts collapsed — "show more" re-expands
  // per search.
  useEffect(() => {
    setShowAllSearch(false)
  }, [deferredSearch, activeTab])

  // Reset state on tab change
  function switchTab(tab: "diseases" | "biomarkers") {
    setActiveTab(tab)
    setActiveCategory(0)
    setSearch("")
    setExpandedItem(null)
    setShowAllItems(false)
  }

  const categories =
    activeTab === "diseases" ? diseaseCategories : biomarkerCategories
  // Pre-select a category once the list is populated (CMS-backed categories
  // arrive async). By explicit slug (e.g. "metabolic" on /go/metabolism), else
  // — when emphasizing the add-on — the add-on-only category, all of whose
  // markers come from an add-on (so the female-hormones LP gets the pre-select
  // without naming the slug). A named slug that resolves wins outright: only a
  // miss (-1) falls through to the auto-pick.
  //
  // Runs at most once — a ref guards it so a later user click isn't yanked
  // back — and after the package-filter reset above, which fires on mount
  // before the CMS data arrives, so this wins the initial index. No smooth
  // scroll, so the page doesn't jump on load.
  const didInitCategory = useRef(false)
  useEffect(() => {
    if (didInitCategory.current || categories.length === 0) return
    let idx = -1
    if (defaultCategorySlug)
      idx = categories.findIndex((c) => c.category === defaultCategorySlug)
    if (idx < 0 && emphasizeAddonCategory)
      idx = categories.findIndex(
        (c) => c.items.length > 0 && c.items.every((i) => isAddonItem(i))
      )
    didInitCategory.current = true
    if (idx > 0) setActiveCategory(idx)
  }, [categories, defaultCategorySlug, emphasizeAddonCategory, isAddonItem])
  const searchPlaceholder =
    activeTab === "diseases" ? ut("searchDiseases") : wt("searchPlaceholder")
  const detailPrefix = lp(
    activeTab === "diseases" ? "/what-we-detect" : "/biomarkers"
  )
  const isSearching = searching

  // Localized name of this explorer's scope (the test/panel it's bound to),
  // for the "No results in {scope}" lead-in on the cross-catalog suggestion.
  const crossScopeKey = PACKAGE_FILTER_ORDER.find(
    (p) => p.id === packages[0]
  )?.translationKey
  const crossScopeLabel = crossScopeKey ? wt(crossScopeKey as never) : undefined

  // Don't render if CMS has no data
  if (!hasDiseases && !hasBiomarkers) return null

  const bloodTest =
    isBloodTest ?? (packages.includes("blood") && !packages.includes("core"))
  const tabContent = activeTab === "diseases" ? diseasesTab : biomarkersTab
  const resolvedHeading =
    tabContent?.heading ??
    (activeTab === "diseases"
      ? tt("diseasesHeading")
      : tt("biomarkersHeading" as never))
  const resolvedSubheading =
    tabContent?.subheading ??
    (activeTab === "diseases"
      ? bloodTest
        ? tt("diseasesSubheadingBlood" as never)
        : tt("diseasesSubheading")
      : tt("biomarkersSubheading" as never))
  const resolvedCta = tabContent?.cta

  const showInnerTabSwitcher =
    allowDiseases && allowBiomarkers && hasDiseases && hasBiomarkers
  const showOuterPanelTabs =
    !!panelTabs && panelTabs.length > 0 && !!activePanel && !!onPanelChange

  // Right-pane content as a plain ReactNode. The pane re-renders directly
  // on category/tab/search change without an outer <AnimatePresence> —
  // that pattern bails to CSR under cacheComponents (see
  // /animation-cache-trap), and wrapping it in <Suspense> creates DOM
  // drift between the fallback and the streamed subtree when an internal
  // useEffect auto-flips the activeTab.
  const rightPaneContent: React.ReactNode = isSearching ? (
    totalSearchHits === 0 ? (
      // No base-test hits. Surface (1) any add-on-only matches as "it's in
      // <add-on>" pointers and (2) the cross-catalog "Did you mean …"
      // suggestion. The "0 results for …" count line sits in the header above.
      <div className="flex flex-col gap-6">
        {addonHintList}
        <div
          className={cn(
            "flex items-center justify-center",
            !addonHintList && "min-h-[45vh]"
          )}
        >
          {crossMatch ? (
            <CrossCatalogSuggestion
              type={activeTab}
              match={crossMatch}
              itemHref={
                activeTab === "biomarkers"
                  ? lp(`/biomarkers/${crossMatch.item.slug}`)
                  : undefined
              }
              testHref={(slug) => lp(`/tests/${slug}`)}
              onSelect={(target) =>
                track.searchSuggestionClicked({
                  query: search.trim(),
                  surface:
                    activeTab === "biomarkers"
                      ? "explorer-biomarkers"
                      : "explorer-diseases",
                  target_test: crossMatch.tests[0].slug,
                  target_type: target,
                })
              }
            />
          ) : null}
        </div>
      </div>
    ) : (
      <div className="flex flex-col">
        {(() => {
          // Cap total results across groups so the card stays ~one screen;
          // the "show more" below lifts the cap to the full list.
          let budget = showAllSearch ? Infinity : SEARCH_RESULT_CAP
          return (searchGroups ?? [])
            .map((g, originalIndex) => ({ g, originalIndex }))
            .filter(({ g }) => g.items.length > 0)
            .map(({ g, originalIndex }, displayIndex, arr) => {
              if (budget <= 0) return null
              const items = g.items.slice(0, budget)
              budget -= items.length
              const highlighted = activeCategory === originalIndex
              const prevHighlighted =
                displayIndex > 0 &&
                activeCategory === arr[displayIndex - 1].originalIndex
              const showDivider =
                displayIndex > 0 && !highlighted && !prevHighlighted
              const labelText = catText(g.category).name
              return (
                <Fragment key={g.category}>
                  {displayIndex > 0 && (
                    <div
                      aria-hidden="true"
                      className={cn(
                        "mx-4 h-px bg-neutral-200 transition-opacity duration-200 md:mx-6",
                        showDivider ? "opacity-100" : "opacity-0"
                      )}
                    />
                  )}
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-4 transition-colors duration-200 md:px-6",
                      highlighted ? "bg-neutral-100" : "bg-transparent"
                    )}
                  >
                    <p className="type-label-md mb-2 text-neutral-400 uppercase">
                      {labelText}
                    </p>
                    <div className="grid grid-cols-1 content-start gap-x-8 lg:grid-cols-2 lg:[&>div:nth-last-child(-n+2)]:border-b-0">
                      {items.map((item) => {
                        const isDiseaseRowGated =
                          g.kind === "diseases" && !diseaseContentEnabled
                        const desc =
                          g.kind === "diseases"
                            ? diseaseContentEnabled
                              ? (item.description ?? undefined)
                              : undefined
                            : item.description
                        // Surface a description-only match: when the query
                        // tokens aren't all in the name, the match came from
                        // the description — show a 1-line highlighted excerpt
                        // under the title (the title gives it context).
                        const nameNorm = normalizeForSearch(item.name)
                        const nameHasAllTokens = searchTokens.every((t) =>
                          nameNorm.includes(normalizeForSearch(t))
                        )
                        const collapsedExcerpt =
                          desc && !nameHasAllTokens ? desc : undefined
                        // Search rows expand like browsing mode: click the
                        // title → reveal the full snippet → "learn more" goes
                        // to the detail page (not a straight navigation).
                        return (
                          <ExpandableRow
                            key={`${g.category}-${item.slug ?? item.name}`}
                            name={item.name}
                            slug={isDiseaseRowGated ? undefined : item.slug}
                            description={desc}
                            expandedKey={`${g.category}-${item.slug ?? item.name}`}
                            expanded={expandedItem}
                            onToggle={setExpandedItem}
                            detailPrefix={detailPrefix}
                            learnMoreLabel={ut("learnMore")}
                            accent="neutral"
                            collapsedExcerpt={collapsedExcerpt}
                            searchTokens={searchTokens}
                          />
                        )
                      })}
                    </div>
                  </div>
                </Fragment>
              )
            })
        })()}
        {!showAllSearch && totalSearchHits > SEARCH_RESULT_CAP && (
          <button
            type="button"
            onClick={() => setShowAllSearch(true)}
            className="type-body-lg text-aubergine-800 hover:text-aubergine-900 mt-6 inline-flex cursor-pointer items-center gap-1.5 font-medium transition-colors"
          >
            {ut("showMore")} ({totalSearchHits - SEARCH_RESULT_CAP})
            <ChevronDownSmIcon />
          </button>
        )}
        {addonHintList && <div className="mt-6">{addonHintList}</div>}
      </div>
    )
  ) : filteredItems.length === 0 && !addonBrowseSnippet ? (
    crossMatch ? (
      <CrossCatalogSuggestion
        type={activeTab}
        match={crossMatch}
        itemHref={
          activeTab === "biomarkers"
            ? lp(`/biomarkers/${crossMatch.item.slug}`)
            : undefined
        }
        testHref={(slug) => lp(`/tests/${slug}`)}
        scopeLabel={crossScopeLabel}
        onSelect={(target) =>
          track.searchSuggestionClicked({
            query: search.trim(),
            surface:
              activeTab === "biomarkers"
                ? "explorer-biomarkers"
                : "explorer-diseases",
            target_test: crossMatch.tests[0].slug,
            target_type: target,
          })
        }
      />
    ) : (
      <p className="type-body-md text-neutral-500">
        {tt("searchResults", { count: 0, query: search })}
      </p>
    )
  ) : (
    <>
      <div className="grid grid-cols-1 content-start gap-x-8 lg:grid-cols-2 lg:[&>div:nth-last-child(-n+2)]:border-b-0">
        {filteredItems.map((item, i) => {
          const hiddenOnMobile = !showAllItems && i >= MOBILE_ITEM_LIMIT
          const hiddenOnDesktop = !showAllItems && i >= DESKTOP_ITEM_LIMIT
          const isDiseaseRowGated =
            activeTab === "diseases" && !diseaseContentEnabled
          const description =
            activeTab === "diseases"
              ? diseaseContentEnabled
                ? ((item as DiseaseItem).description ?? undefined)
                : undefined
              : (item as BiomarkerItem).description
          return (
            <ExpandableRow
              key={item.slug ?? item.name}
              name={item.name}
              slug={isDiseaseRowGated ? undefined : item.slug}
              description={description}
              expandedKey={item.slug ?? item.name}
              expanded={expandedItem}
              onToggle={setExpandedItem}
              detailPrefix={detailPrefix}
              learnMoreLabel={ut("learnMore")}
              accent="neutral"
              className={cn(
                hiddenOnDesktop
                  ? "hidden"
                  : hiddenOnMobile
                    ? "hidden md:block"
                    : ""
              )}
            />
          )
        })}
      </div>
      {!showAllItems && filteredItems.length > MOBILE_ITEM_LIMIT && (
        <button
          type="button"
          onClick={() => setShowAllItems(true)}
          className={cn(
            "type-body-lg text-aubergine-800 hover:text-aubergine-900 mt-6 inline-flex cursor-pointer items-center gap-1.5 font-medium transition-colors",
            filteredItems.length <= DESKTOP_ITEM_LIMIT && "md:hidden"
          )}
        >
          <span className="md:hidden">
            {ut("showMore")} ({filteredItems.length - MOBILE_ITEM_LIMIT})
          </span>
          {filteredItems.length > DESKTOP_ITEM_LIMIT && (
            <span className="hidden md:inline">
              {ut("showMore")} ({filteredItems.length - DESKTOP_ITEM_LIMIT})
            </span>
          )}
          <ChevronDownSmIcon />
        </button>
      )}
      {addonBrowseSnippet}
    </>
  )

  return (
    <LazyMotion features={domMax}>
      <section id={id} className={cn(!hideHeader && "py-12 md:py-20")}>
        <div
          className={cn(
            "mx-auto flex max-w-[1440px] flex-col items-center gap-10 max-md:gap-8",
            // When embedded under a consumer's section (hideHeader), that
            // section already supplies the horizontal padding — don't add a
            // second layer, or the explorer indents further than the
            // surrounding header/CTA.
            hideHeader ? "px-0" : "px-4 md:px-10"
          )}
        >
          {/* Full-bleed white band — the color lives on the <section>; this
            just lays out the heading → grid, centered in the content width. */}
          <div className="flex w-full flex-col items-center gap-6">
            {!hideHeader && (
              <div>
                <div className="flex max-w-[720px] flex-col items-center gap-3 text-center md:gap-4">
                  {eyebrow && (
                    <p className="type-label-sm font-medium text-neutral-900/70 uppercase">
                      {eyebrow}
                    </p>
                  )}
                  <h2 className={cn(headingSizeClass, "text-neutral-900")}>
                    {resolvedHeading}
                  </h2>
                  {/* Lock to two lines so the surrounding layout doesn't jump
                  when tab/panel switches swap a 1-line subheading for a
                  2-line one. `2lh` is exactly two line-boxes of the
                  inherited line-height, so it fits both states without
                  guessing pixel values per breakpoint. */}
                  <p className="type-body-lg mx-auto min-h-[2lh] max-w-[520px] text-neutral-900/70">
                    {resolvedSubheading}
                  </p>
                </div>
              </div>
            )}

            {/* Outer panel switcher (e.g. membership baseline / mid-year). */}
            {showOuterPanelTabs && (
              <div className="max-md:bg-background/90 max-md:sticky max-md:top-[72px] max-md:z-30 max-md:flex max-md:w-full max-md:justify-center max-md:backdrop-blur-md">
                <div>
                  <TabSwitcher
                    tabs={panelTabs!}
                    activeTab={activePanel!}
                    onChange={onPanelChange!}
                    className="bg-[rgba(200,200,200,0.23)] backdrop-blur-[30px] max-md:[&_button]:pr-5 max-md:[&_button]:pl-5 max-md:[&_img]:hidden" // ds-color-ignore: translucent overlay
                  />
                </div>
              </div>
            )}

            {/* Diseases/biomarkers switcher — centered. Search lives in the
              left rail, above the category pills. */}
            {showInnerTabSwitcher && (
              <div className="flex w-full flex-wrap items-center justify-center gap-2">
                <TabSwitcher
                  tabs={[
                    {
                      value: "diseases",
                      label: wt("tabHealthRisks"),
                      // Count goes in the badge slot, not the label: the label
                      // sits in a truncating span, so on a phone a long
                      // translation ("Gezondheidsrisico's", "Gesundheitsrisiken")
                      // used to eat the figure the tab exists to advertise.
                      badge: `(${diseaseCategories.reduce(
                        (n, c) => n + c.items.length,
                        0
                      )})`,
                      icon: <VirusIcon className="size-6" />,
                    },
                    {
                      value: "biomarkers",
                      label: wt("tabBiomarkers"),
                      badge: `(${biomarkerCategories.reduce(
                        (n, c) =>
                          n + c.items.filter((b) => !isAddonItem(b)).length,
                        0
                      )})`,
                      icon: <TestTubeIcon className="size-6" />,
                    },
                    // Peer tab into the proxy-gated /symptoms lens. Shown only to
                    // users who can reach it (public or preview-token cookie), so
                    // it never leaks a 404 to the public. Selecting it swaps in
                    // the symptoms lens inline (same as biomarkers/conditions)
                    // rather than navigating away.
                    ...(showSymptomsTab
                      ? [
                          {
                            value: "symptoms",
                            label: st("tabLabel"),
                            badge: `(${scopedSymptomCount})`,
                            icon: <BrainIcon className="size-6" />,
                          },
                        ]
                      : []),
                  ]}
                  activeTab={symptomsMode ? "symptoms" : activeTab}
                  onChange={(v) => {
                    if (v === "symptoms") {
                      setSymptomsMode(true)
                    } else {
                      setSymptomsMode(false)
                      switchTab(v as "diseases" | "biomarkers")
                    }
                  }}
                />
              </div>
            )}

            {/* MRI package filter chips (Core / Advanced / Pro) — mirrors the
              /biomarkers listing chips, scoped to this test's own packages.
              Filters both the biomarkers and health-risks tabs. */}
            {showPackageFilter && !symptomsMode && (
              <div
                role="group"
                className="flex w-full flex-wrap items-center justify-center gap-2"
              >
                <button
                  type="button"
                  aria-pressed={activePackageFilter === null}
                  onClick={() => setActivePackageFilter(null)}
                  className={cn(
                    "flex-shrink-0 cursor-pointer rounded-full border-[1.5px] px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-all duration-200",
                    activePackageFilter === null
                      ? "border-aubergine-800 bg-aubergine-800 text-white"
                      : "hover:border-aubergine-400 hover:bg-aubergine-100 border-neutral-200 bg-white text-neutral-600"
                  )}
                >
                  {wt("filterAll")}
                </button>
                {packageFilters.map((pkg) => {
                  const isActive = activePackageFilter === pkg.id
                  const variant = PACKAGE_ICON_VARIANT[pkg.id]
                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() =>
                        setActivePackageFilter(isActive ? null : pkg.id)
                      }
                      className={cn(
                        "flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-full border-[1.5px] py-1 pr-4 pl-1 text-sm font-medium whitespace-nowrap transition-all duration-200",
                        isActive
                          ? "border-aubergine-800 bg-aubergine-800 text-white"
                          : "hover:border-aubergine-400 hover:bg-aubergine-100 border-neutral-200 bg-white text-neutral-600"
                      )}
                    >
                      {variant && (
                        <PackageIcon
                          variant={variant}
                          size="sm"
                          className="!h-[26px] !w-[26px] !rounded-full"
                        />
                      )}
                      {pkg.label}
                    </button>
                  )
                })}
              </div>
            )}

            {panelNote}

            {symptomsMode && showSymptomsTab ? (
              <SymptomsExplorer
                embedded
                groups={cmsSymptomGroups}
                symptoms={cmsSymptoms}
                symptomPackages={symptomPackages}
                scopePackages={packages}
                locale={locale}
              />
            ) : null}

            <div
              ref={sectionRef}
              className={cn(
                "grid w-full grid-cols-1 gap-6",
                layoutMode === "sidebar" && "md:grid-cols-[280px_1fr] md:gap-8",
                symptomsMode && "hidden"
              )}
            >
              {/* LEFT — search pill + category pills */}
              <div
                className={cn(
                  "flex w-full flex-col items-start gap-4",
                  layoutMode === "sidebar" &&
                    "md:sticky md:top-20 md:self-start"
                )}
              >
                {/* Search — pulled out of the category <ul> so the categories
                  can scroll horizontally as their own row on mobile. The rail's
                  `gap-4` provides the 16px search→categories gap. */}
                <div className="w-full max-w-full">
                  <label className="flex w-full max-w-full cursor-text items-center gap-3 rounded-[28px] bg-[rgba(64,64,64,0.05)] px-4 py-3 transition-colors focus-within:bg-white focus-within:ring-1 focus-within:ring-neutral-300 hover:bg-[rgba(64,64,64,0.09)]">
                    {/* ds-color-ignore: translucent overlay (alpha) */}
                    <span className="flex size-5 shrink-0 items-center justify-center text-neutral-500">
                      <SearchIcon width={20} height={20} />
                    </span>
                    <input
                      type="search"
                      aria-label={searchPlaceholder}
                      autoComplete="off"
                      placeholder={searchPlaceholder}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      // 16px min font-size (type-title-md) so iOS Safari doesn't
                      // auto-zoom on focus — 14px (type-title-sm) triggered a zoom
                      // that broke the mobile layout.
                      className="type-title-md w-full min-w-0 bg-transparent text-neutral-900 outline-none placeholder:font-normal placeholder:text-neutral-400 [&::-webkit-search-cancel-button]:appearance-none"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          setSearch("")
                        }}
                        aria-label={ut("clearSearch")}
                        className="-mr-1 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-200/60 hover:text-neutral-900"
                      >
                        <CloseIcon width={18} height={18} />
                      </button>
                    )}
                  </label>
                </div>
                {/* Category rail — horizontal scroll on mobile (a single
                  swipeable row), vertical list on the desktop sidebar. */}
                <ul
                  className={cn(
                    "flex w-full [scrollbar-width:none] gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden",
                    layoutMode === "sidebar"
                      ? "md:flex-col md:gap-1.5 md:overflow-x-visible"
                      : "md:flex-wrap md:gap-2 md:overflow-x-visible"
                  )}
                >
                  {/* Category list — plain text rows (no pill fills, no
                    dividers). The active row is highlighted with weight +
                    full-strength ink; inactive rows sit muted. The selected
                    category is also surfaced on the content side (right pane
                    heading). */}
                  {categories.map((cat, index) => {
                    const isActive = activeCategory === index
                    const labelText = catText(cat.category).name
                    const catCms = catCmsDoc(cat.category)
                    const totalCount = cat.items.filter(
                      (i) => !isAddonItem(i)
                    ).length
                    const matchCount =
                      isSearching && searchGroups
                        ? (searchGroups[index]?.items.length ?? 0)
                        : null
                    const dimmed = isSearching && matchCount === 0
                    const displayCount = matchCount ?? totalCount
                    // Addon-only category — no base markers, every item comes
                    // from an add-on (e.g. Hormones / Omega on the blood test).
                    // Gets a small "Add-on" badge next to its name in the rail.
                    const isAddonOnly = totalCount === 0 && cat.items.length > 0
                    return (
                      <li
                        key={cat.category}
                        className="shrink-0 md:w-full md:max-w-full"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            navigateToCategory(index)
                          }}
                          aria-current={isActive ? "true" : undefined}
                          className={cn(
                            "group flex w-fit max-w-full cursor-pointer items-center gap-3 rounded-full px-4 py-2.5 text-left whitespace-nowrap text-neutral-900 transition-colors md:whitespace-normal",
                            isActive
                              ? "bg-white"
                              : "hover:bg-[rgba(64,64,64,0.05)]", // ds-color-ignore: translucent overlay (alpha)
                            dimmed && "opacity-40"
                          )}
                        >
                          <span className="hidden size-6 shrink-0 md:block [&_svg]:h-full [&_svg]:w-full [&_svg]:overflow-visible">
                            {renderCategoryIcon({
                              categoryId: cat.category,
                              cmsIcon: catCms?.icon,
                              cmsColor: catCms?.color,
                              size: 24,
                            })}
                          </span>
                          <span className="flex items-baseline gap-2">
                            {/* Count sits inline inside the label so a wrapped
                              long label ("Knochen- und Muskelgesundheit")
                              keeps the count after its last word instead of
                              pinning it top-right as a separate flex item. */}
                            <span className="type-title-md">
                              {labelText}
                              {(isSearching || displayCount > 0) && (
                                <span
                                  aria-hidden="true"
                                  className="hidden pl-2 font-normal whitespace-nowrap text-neutral-400 md:inline"
                                >
                                  {displayCount}
                                </span>
                              )}
                            </span>
                            {isAddonOnly && (
                              <ActionBadge
                                label={ut("addonLabel")}
                                color="neutral"
                                className={cn(
                                  "self-center",
                                  emphasizeAddonCategory
                                    ? "bg-neutral-900 text-white"
                                    : cn(
                                        "text-neutral-700",
                                        isActive
                                          ? "bg-neutral-50"
                                          : "bg-neutral-100 group-hover:bg-neutral-50"
                                      )
                                )}
                              />
                            )}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>

              {/* RIGHT — marker / disease list. The right-pane content
              re-renders directly when the active category, tab, or search
              query changes. We deliberately don't wrap this in an outer
              <AnimatePresence mode="wait"> for the fade-on-switch — that
              pattern + cacheComponents bails the whole page to CSR (see
              /animation-cache-trap), and the auto-switch from biomarkers
              ↔ diseases on /tests/[slug] mounts left mismatched DOM
              between the Suspense fallback and the streamed subtree.
              ExpandableRow's per-item AnimatePresence is fine because
              it wraps a *conditionally rendered* child. */}
              <div className="flex flex-col rounded-[24px] bg-white p-5 md:p-8">
                <div className="flex flex-1 flex-col">
                  <div>
                    {(() => {
                      const activeCat = categories[activeCategory]
                      if (!activeCat) return null
                      const activeCatText = catText(activeCat.category)
                      const activeDescText =
                        categoryBlurbs?.[activeCat.category] ??
                        (activeCatText.description || null)
                      const activeLabelText = activeCatText.name
                      const activeCatCms = catCmsDoc(activeCat.category)
                      // Addon-only category: no base markers, every item lives
                      // in a separate add-on (Hormones / Omega on the blood
                      // test). We swap the "Key biomarkers we check for N" label
                      // for an explainer instead of showing a bare "0".
                      const activeBaseCount = activeCat.items.filter(
                        (i) => !isAddonItem(i)
                      ).length
                      const activeIsAddonOnly =
                        activeBaseCount === 0 && activeCat.items.length > 0
                      return (
                        <m.div
                          key={`${activeTab}-${activeCategory}`}
                          initial={{ opacity: 0, y: 14 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.4,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                        >
                          <div className="mb-3 hidden flex-col gap-4 md:flex">
                            {isSearching ? (
                              /* Search mode: the browsing chrome (category
                                icon / name / blurb + "we check for N") is
                                replaced by a single "Search results" heading,
                                and the next-section peek is suppressed below. */
                              <div className="flex flex-col gap-2">
                                <h3 className="type-display-sm text-neutral-900">
                                  {tt("searchResultsHeading")}
                                </h3>
                                {/* Result count for the query — shown for both
                                  hits and zero ("3 results for …" / "0 results
                                  for …"). */}
                                <p className="type-body-md text-neutral-400">
                                  {tt("searchResults", {
                                    count: totalSearchHits,
                                    query: search,
                                  })}
                                </p>
                              </div>
                            ) : (
                              <>
                                {/* Heading row: category icon + name (the count
                                  sits in the "we check for N" label below). */}
                                <div className="flex items-center gap-3">
                                  <m.span
                                    className="size-8 shrink-0 text-neutral-800 [&_svg]:h-full [&_svg]:w-full [&_svg]:overflow-visible"
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{
                                      type: "spring",
                                      stiffness: 340,
                                      damping: 30,
                                      delay: 0.18,
                                    }}
                                  >
                                    {renderCategoryIcon({
                                      categoryId: activeCat.category,
                                      cmsIcon: activeCatCms?.icon,
                                      cmsColor: activeCatCms?.color,
                                      size: 32,
                                    })}
                                  </m.span>
                                  <h3 className="type-display-sm text-neutral-900">
                                    {activeLabelText}
                                  </h3>
                                  {emphasizeAddonCategory &&
                                    activeIsAddonOnly && (
                                      <ActionBadge
                                        label={ut("addonLabel")}
                                        color="neutral"
                                        size="md"
                                        className="self-center bg-neutral-900 text-white"
                                      />
                                    )}
                                </div>
                                {activeDescText && (
                                  <p className="type-body-lg max-w-[640px] text-neutral-700">
                                    {activeDescText}
                                  </p>
                                )}
                                {(!activeIsAddonOnly ||
                                  emphasizeAddonCategory) && (
                                  <div className="flex items-baseline gap-2">
                                    <span className="type-title-md text-neutral-900">
                                      {activeTab === "biomarkers"
                                        ? wt("keyBiomarkersLabel")
                                        : wt("keyRisksLabel")}
                                    </span>
                                    <span className="type-body-md text-neutral-400">
                                      {activeIsAddonOnly &&
                                      emphasizeAddonCategory
                                        ? activeCat.items.length
                                        : activeBaseCount}
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {rightPaneContent}
                        </m.div>
                      )
                    })()}
                  </div>
                  {(() => {
                    // No next-section peek while searching — search mode shows
                    // a flat, cross-category result list instead.
                    if (showAllItems || isSearching) return null
                    const nextIndex = (activeCategory + 1) % categories.length
                    const nextCat = categories[nextIndex]
                    if (!nextCat || nextIndex === activeCategory) return null
                    const nextLabelText = catText(nextCat.category).name
                    // Up to a 2-row tease of the next section (2-col grid),
                    // faded out by the gradient below. Kept short so a
                    // marker-heavy active category (e.g. Cardiovascular) doesn't
                    // push the card taller than the category rail beside it.
                    const previewItems = nextCat.items
                      .filter((i) => !isAddonItem(i))
                      .slice(0, 4)
                    return (
                      <>
                        {/* Clickable tease of the next section. Flows directly
                          after the active list (no gap) so it reads as "what's
                          next", not a footer. Brightens on hover, navigates on
                          click; aria-hidden inner content — the wrapper carries
                          the "Next: ‹label›" label. */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => navigateToCategory(nextIndex)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault()
                              navigateToCategory(nextIndex)
                            }
                          }}
                          aria-label={ut("nextCategoryAria", {
                            label: nextLabelText,
                          })}
                          className="group relative mt-8 hidden cursor-pointer border-t border-neutral-200 pt-8 text-left focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:outline-none md:block"
                        >
                          <div className="relative" aria-hidden="true">
                            <div className="opacity-50 transition-opacity duration-200 group-hover:opacity-80">
                              <div className="flex items-center gap-3">
                                <span className="type-title-lg text-neutral-900">
                                  {nextLabelText}
                                </span>
                                <span className="type-body-md text-neutral-500">
                                  {
                                    nextCat.items.filter((i) => !isAddonItem(i))
                                      .length
                                  }{" "}
                                  {activeTab === "biomarkers"
                                    ? wt("unitBiomarkers")
                                    : wt("unitRisks")}
                                </span>
                              </div>
                              <div className="mt-4 grid grid-cols-1 gap-x-8 text-neutral-700 lg:grid-cols-2">
                                {previewItems.map((item) => (
                                  <div
                                    key={item.name}
                                    className="type-body-lg py-1.5"
                                  >
                                    {item.name}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" />
                          </div>
                        </div>
                        {/* "Next: ‹label›" pill — pushed to the bottom of the
                          card (mt-auto) so it stays anchored even when the grid
                          stretches the card to the taller left rail. */}
                        <div className="mt-auto hidden justify-center pt-6 md:flex">
                          <button
                            type="button"
                            onClick={() => navigateToCategory(nextIndex)}
                            aria-label={ut("nextCategoryAria", {
                              label: nextLabelText,
                            })}
                            className="group inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-100 bg-white px-5 py-3 text-neutral-600 shadow-[0_2px_11px_rgba(0,0,0,0.04)] transition-colors hover:border-neutral-300 hover:text-neutral-900"
                          >
                            <span className="type-title-sm">
                              {ut("nextCategoryAria", { label: nextLabelText })}
                            </span>
                            <ChevronRightSmIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
                          </button>
                        </div>
                      </>
                    )
                  })()}
                </div>
              </div>
            </div>
          </div>

          {resolvedCta &&
            (resolvedCta.variant === "outline" ? (
              <div>
                <Link
                  href={resolvedCta.href}
                  className={cn(
                    buttonVariants({ variant: "outline" }),
                    "self-center"
                  )}
                >
                  {resolvedCta.label}
                  <ChevronRightSmIcon className="ml-2" />
                </Link>
              </div>
            ) : resolvedCta.variant === "text" ? (
              <div>
                <Link href={resolvedCta.href}>
                  <Button
                    variant="text"
                    className="group -ml-4"
                    iconRight={
                      <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                    }
                  >
                    {resolvedCta.label}
                  </Button>
                </Link>
              </div>
            ) : (
              <div>
                <Link
                  href={resolvedCta.href}
                  className="type-title-sm text-aubergine-800 hover:text-aubergine-900 inline-flex items-center gap-1 transition-[gap] hover:gap-2"
                >
                  {resolvedCta.label} &rarr;
                </Link>
              </div>
            ))}
        </div>
      </section>
    </LazyMotion>
  )
}
